/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ImageResolution } from '../models/ImageResolution';
import type { PriceScaleTableDto } from '../models/PriceScaleTableDto';
import type { PrintPositionPriceDetailsDto } from '../models/PrintPositionPriceDetailsDto';
import type { ProductCertificateDto } from '../models/ProductCertificateDto';
import type { ProductDocumentDto } from '../models/ProductDocumentDto';
import type { ProductVariantDto } from '../models/ProductVariantDto';
import type { SpecificationsDto } from '../models/SpecificationsDto';
import type { SubscriptionDto } from '../models/SubscriptionDto';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class ProductService {

    /**
     * @returns ProductVariantDto Returns array of product variants
     * @throws ApiError
     */
    public static getShowProductVariants({
        locale,
        productCode,
    }: {
        /**
         * Locale
         */
        locale: string,
        productCode: string,
    }): CancelablePromise<Array<ProductVariantDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{productCode}/variants',
            path: {
                'productCode': productCode,
            },
            query: {
                'locale': locale,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns ProductDocumentDto Returns an array of documents for the product.
     * @throws ApiError
     */
    public static getShowProductDocuments({
        locale,
        productCode,
    }: {
        /**
         * Locale
         */
        locale: string,
        productCode: string,
    }): CancelablePromise<Array<ProductDocumentDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{productCode}/documents',
            path: {
                'productCode': productCode,
            },
            query: {
                'locale': locale,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns PrintPositionPriceDetailsDto Returns array of Price detail table data, for each print position
     * @throws ApiError
     */
    public static getShowProductPrintOptionPriceScales({
        locale,
        code,
        debtor,
        country,
    }: {
        /**
         * Locale
         */
        locale: string,
        code: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Country code
         */
        country?: string,
    }): CancelablePromise<Array<PrintPositionPriceDetailsDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/print-option-price-scales',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'debtor': debtor,
                'country': country,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns PriceScaleTableDto Returns ranges with a singular price scale for the product
     * @throws ApiError
     */
    public static getShowProductVariantPriceScale({
        locale,
        code,
        debtor,
        country,
    }: {
        /**
         * Locale
         */
        locale: string,
        code: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Country code
         */
        country?: string,
    }): CancelablePromise<PriceScaleTableDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/variant-price-scale',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'debtor': debtor,
                'country': country,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns PriceScaleTableDto Returns ranges and price scales for the product
     * @throws ApiError
     */
    public static getShowProductVariantPriceScales({
        locale,
        code,
        debtor,
        country,
    }: {
        /**
         * Locale
         */
        locale: string,
        code: string,
        /**
         * Debtor code
         */
        debtor?: string,
        /**
         * Country code
         */
        country?: string,
    }): CancelablePromise<PriceScaleTableDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/variant-price-scales',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
                'debtor': debtor,
                'country': country,
            },
            errors: {
                401: `Unauthorized`,
                403: `Access denied.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns binary Returns a download url for a zip file containing product datasheets
     * @throws ApiError
     */
    public static getDownloadDatasheets({
        variantCodes,
        locale,
        productCode,
    }: {
        /**
         * Variant codes
         */
        variantCodes: Array<string>,
        /**
         * Locale
         */
        locale: string,
        productCode: string,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{productCode}/datasheets',
            path: {
                'productCode': productCode,
            },
            query: {
                'variantCodes[]': variantCodes,
                'locale': locale,
            },
            errors: {
                400: `Incorrect query parameters.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns string URL for downloading product sheets.
     * @throws ApiError
     */
    public static getDownloadDatasheetsUrl({
        variantCodes,
        locale,
        productCode,
    }: {
        /**
         * Variant codes
         */
        variantCodes: Array<string>,
        /**
         * Locale
         */
        locale: string,
        productCode: string,
    }): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{productCode}/datasheets-url',
            path: {
                'productCode': productCode,
            },
            query: {
                'variantCodes[]': variantCodes,
                'locale': locale,
            },
            errors: {
                400: `Incorrect query parameters.`,
            },
        });
    }

    /**
     * @returns binary Returns a download url for a zip file containing product images
     * @throws ApiError
     */
    public static getGetProductImages({
        code,
        resolutions,
    }: {
        code: string,
        resolutions: Array<ImageResolution>,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/product-images',
            path: {
                'code': code,
            },
            query: {
                'resolutions[]': resolutions,
            },
            errors: {
                400: `Product code is not valid.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns string URL for downloading product variant images.
     * @throws ApiError
     */
    public static getGetProductImagesUrl({
        resolutions,
        code,
    }: {
        /**
         * Image resolutions
         */
        resolutions: Array<ImageResolution>,
        code: string,
    }): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/product-images-url',
            path: {
                'code': code,
            },
            query: {
                'resolutions[]': resolutions,
            },
            errors: {
                400: `Incorrect query parameters.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns binary Returns a download url for a zip file containing variant images
     * @throws ApiError
     */
    public static getGetProductVariantImages({
        resolutions,
        variantCodes,
        code,
    }: {
        /**
         * Image resolutions
         */
        resolutions: Array<ImageResolution>,
        /**
         * Variant codes
         */
        variantCodes: Array<string>,
        code: string,
    }): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/variant-images',
            path: {
                'code': code,
            },
            query: {
                'resolutions[]': resolutions,
                'variantCodes[]': variantCodes,
            },
            errors: {
                400: `Product code is not valid.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns string URL for downloading product variant images.
     * @throws ApiError
     */
    public static getGetProductVariantImagesUrl({
        resolutions,
        variantCodes,
        code,
    }: {
        /**
         * Image resolutions
         */
        resolutions: Array<ImageResolution>,
        /**
         * Variant codes
         */
        variantCodes: Array<string>,
        code: string,
    }): CancelablePromise<string> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/variant-images-url',
            path: {
                'code': code,
            },
            query: {
                'resolutions[]': resolutions,
                'variantCodes[]': variantCodes,
            },
            errors: {
                400: `Incorrect query parameters.`,
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns ProductCertificateDto Returns a url for the product certificates.
     * @throws ApiError
     */
    public static getShowProductCertificateUrl({
        productCode,
    }: {
        productCode: string,
    }): CancelablePromise<ProductCertificateDto> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{productCode}/certificate-url',
            path: {
                'productCode': productCode,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns any Returns 200 when successfully registered for stock alert
     * @throws ApiError
     */
    public static postSaveProductVariantStockAlert({
        locale,
        productCode,
        variantCode,
        requestBody,
    }: {
        /**
         * Locale
         */
        locale: string,
        productCode: string,
        variantCode: string,
        requestBody: SubscriptionDto,
    }): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/v1/products/{productCode}/variants/{variantCode}/stock-alerts',
            path: {
                'productCode': productCode,
                'variantCode': variantCode,
            },
            query: {
                'locale': locale,
            },
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                404: `Product not found.`,
            },
        });
    }

    /**
     * @returns SpecificationsDto Returns an array of grouped specificfations with a title for giving product.
     * @throws ApiError
     */
    public static getShowProductSpecs({
        locale,
        code,
    }: {
        /**
         * Locale
         */
        locale: string,
        code: string,
    }): CancelablePromise<Array<SpecificationsDto>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/v1/products/{code}/specifications',
            path: {
                'code': code,
            },
            query: {
                'locale': locale,
            },
            errors: {
                404: `Product not found.`,
            },
        });
    }

}
